import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Subject, take } from 'rxjs';
import { RefreshTokenAction, RefreshTokenSuccessAction } from '../store/user/user.actions';
import { AuthService } from './auth.service';
import { Router } from '@angular/router';
import { Actions, ofType } from '@ngrx/effects';

@Injectable({
    providedIn: 'root'
})
export class ParentalGateSessionService {

    Session_Expiry_Time = 5; // session expiry time in minutes
    private readonly SESSION_KEY = 'parental-gate-expiry';

    private expiryTimer?: ReturnType<typeof setTimeout>;

    /**
     * Emits whenever the parental gate session expires.
     */
    readonly sessionExpired$ = new Subject<void>();

    constructor(private store: Store, private authService: AuthService, private actions$: Actions, private router: Router) {
        this.restoreParentGateAccessTimer();
    }

    /**
     * Start Parental Gate session
     */
    startSession(minutes = this.Session_Expiry_Time): void {

        const expiry = Date.now() + minutes * 60 * 1000;

        sessionStorage.setItem(this.SESSION_KEY, expiry.toString());

        this.startExpiryTimer(expiry);
    }

    /**
     * Returns true if user has already verified.
     */
    isParentalGateSessionActive(): boolean {

        const expiry = Number(sessionStorage.getItem(this.SESSION_KEY));

        if (!expiry) {
            return false;
        }

        if (Date.now() >= expiry) {
            this.expireSession();
            return false;
        }

        return true;
    }

    clearSession(): void {
        this.expireSession();
    }

    getRemainingSeconds(): number {

        const expiry = Number(sessionStorage.getItem(this.SESSION_KEY));

        if (!expiry) {
            return 0;
        }

        return Math.max(0, Math.ceil((expiry - Date.now()) / 1000));
    }

    /**
     * Restarts timer after page refresh.
     */
    restoreParentGateAccessTimer(): void {
        const expiry = Number(sessionStorage.getItem(this.SESSION_KEY));
        
        if (!expiry || expiry <= Date.now()) {
            this.expireSession();
            return;
        }

        this.startExpiryTimer(expiry);
    }

    private startExpiryTimer(expiry: number): void {

        clearTimeout(this.expiryTimer);

        const remaining = expiry - Date.now();

        this.expiryTimer = setTimeout(() => {
            this.expireSession();
        }, remaining);
    }

    private expireSession(): void {
        clearTimeout(this.expiryTimer);
        
        sessionStorage.removeItem(this.SESSION_KEY);

        // reload current page after token refresh to ask for Parental Access again
        this.actions$.pipe(
            ofType(RefreshTokenSuccessAction),
            take(1)
        ).subscribe(() => {
            this.router.navigateByUrl(this.router.url);
        });

        // Update the token to remove parental access on session expire
        this.store.dispatch(RefreshTokenAction({
            request: {
                Token: this.authService.getTokenInformation()?.RefreshToken!,
                RefreshParentalControl: false
            }
        }));
        this.authService.tokenRefreshedByParentalGate();

        this.sessionExpired$.next();
    }
}